import { fn } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type { LookerDashboard, LookerFolder } from 'lumen/models';

interface MarketCustomReportingFormRemoveSignature {
  Args: {
    afterRemove?: () => void;
    close: () => void;
    item: LookerFolder | LookerDashboard;
    type: string;
  };
}

export default class MarketCustomReportingFormRemove extends Component<MarketCustomReportingFormRemoveSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @tracked confirmValue?: string;

  removeTask = task({ drop: true }, async (): Promise<void> => {
    const { item, close, afterRemove } = this.args;

    try {
      await item.destroyRecord();
      afterRemove?.();
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRemoveDisabled() {
    const { item, type } = this.args;

    if (type === 'dashboard') {
      return (
        this.confirmValue?.toLowerCase() !==
        (item as LookerDashboard).title.toLowerCase()
      );
    }

    return (
      this.confirmValue?.toLowerCase() !==
      (item as LookerFolder).name.toLowerCase()
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get messageContent() {
    const { item, type } = this.args;

    if (type === 'dashboard') {
      const { title } = item as LookerDashboard;

      return {
        title: this.intl.t('custom_reporting.delete_dashboard.title', {
          title,
        }),
        description: this.intl.t(
          'custom_reporting.delete_dashboard.description',
        ),
        confirmLabel: this.intl.t('custom_reporting.form.delete_confirm', {
          title,
        }),
      };
    }

    const { name: title } = item as LookerFolder;
    return {
      title: this.intl.t('custom_reporting.delete_folder.title', {
        title,
      }),
      description: this.intl.t('custom_reporting.delete_folder.description'),
      confirmLabel: this.intl.t('custom_reporting.form.delete_confirm', {
        title,
      }),
    };
  }

  <template>
    <PrismModal
      @onClose={{@close}}
      class="custom-reporting__modal custom-reporting__modal--delete-confirmation"
      data-test-id="delete-confirm-modal"
    >
      <:default>
        <div class="modal--padded custom-reporting-form">
          <div class="flex justify-center">
            <PrismMessage
              @description={{this.messageContent.description}}
              @illustration="100x100_warning"
              @title={{this.messageContent.title}}
              data-test-id="delete-confirm-message"
            />
          </div>
          <div class="flex w-full flex-row">
            <PrismInput
              @label={{this.messageContent.confirmLabel}}
              @onInput={{pick "target.value" (fn (mut this.confirmValue))}}
              @value={{this.confirmValue}}
              data-test-id="delete-confirm-input"
            />
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@close}}
          />
          <PrismButton
            @buttonType="critical"
            @isDisabled={{or this.isRemoveDisabled this.removeTask.isRunning}}
            @isLoading={{this.removeTask.isRunning}}
            @label={{t "custom_reporting.form.delete_forever"}}
            @onClick={{this.removeTask.perform}}
            data-test-id="delete-confirm-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
